import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Injector, Input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { DateUtils } from '@nimbus-library/features/shared/utils/date.utils';

@Component({
  standalone: false,
  selector: 'nimbus-input-time-picker',
  templateUrl: './input-time-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputTimePickerComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => InputTimePickerComponent),
    },
  ],
})
export class InputTimePickerComponent extends IInputComponent<Date> implements Validator {
  @Input()
  public format: string = 'HH:mm:ss';
  @Input()
  public floatLabel: 'auto' | 'always' = 'auto';

  private innerMin: Date;
  private innerMax: Date;

  @Input()
  public get value(): Date {
    return this.innerValue;
  }
  public set value(value: Date) {
    if (value && (value.constructor.name === 'String' || value.constructor.name === 'Number')) {
      value = new Date(value);
    }
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.propagateChange(value);
    }
  }

  @Input()
  public set min(value: Date) {
    if (value && (value.constructor.name === 'String' || value.constructor.name === 'Number')) {
      this.innerMin = new Date(value);
    } else if (value) {
      this.innerMin = value;
    }
  }
  public get min(): Date {
    return this.innerMin;
  }

  @Input()
  public set max(value: Date) {
    if (value && (value.constructor.name === 'String' || value.constructor.name === 'Number')) {
      this.innerMax = new Date(value);
    } else if (value) {
      this.innerMax = value;
    }
  }
  public get max(): Date {
    return this.innerMax;
  }

  public constructor(
    private readonly changeDetectionRef: ChangeDetectorRef,
    injector: Injector,
    translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public writeValue(value: Date): void {
    if (value && (value.constructor.name === 'String' || value.constructor.name === 'Number')) {
      value = new Date(value);
    }
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.changeDetectionRef.markForCheck();
  }

  public validate(control: AbstractControl): ValidationErrors {
    let result: ValidationErrors = null;
    if (control && control.value) {
      const duration = DateUtils.durationFromDate(control.value);
      if (this.max && duration > DateUtils.durationFromDate(this.max)) {
        result = { max: { max: DateUtils.formatDate(this.max, this.format) } };
      } else if (this.min && duration < DateUtils.durationFromDate(this.min)) {
        result = { min: { min: DateUtils.formatDate(this.min, this.format) } };
      }
    } else {
      result = null;
    }
    return result;
  }
}
